// Optimistic-update store for in-flight trades: a receipt-confirmed tx that
// hasn't shown up in backend query results (positions/trades/history) yet.
// No persist — bigint-adjacent amounts + a page refresh should fall back to
// server truth rather than resurrect a stale optimistic row.
import { OrderSide } from '@/libs/sdk/api';
import { combine } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

export type PendingTradeStatus = 'pending' | 'syncSlow';
export type PendingTradeSide = 'buy' | 'sell';

export type PendingTrade = {
  txHash: string; // unique key, stored lowercase
  uid: string;
  marketAddress: string; // lowercase
  optionAddress: string; // lowercase
  side: PendingTradeSide;
  amountIn: string; // raw units; buy=base token in, sell=option shares in. Receipt event value when decodable, else form value
  amountOut?: string; // raw units from receipt event; undefined = form fallback
  price?: string; // MarketDetailOption.price at submit (raw string)
  decimal: number; // base token decimal
  optionDecimal?: number;
  baseTokenAddress: string; // lowercase
  marketName?: string;
  optionName?: string;
  status: PendingTradeStatus;
  createdAt: number; // Date.now() ms
};

export type NewPendingTrade = Omit<PendingTrade, 'status' | 'createdAt'> & { createdAt?: number };

type PendingTradesState = {
  trades: Record<string, PendingTrade>;
  // uuids of backend orders already claimed as the reconciled match for some
  // pending trade — kept so a second, similar trade (same uid/side/market/
  // amount within the lookback window) can't re-match the SAME order that
  // already satisfied an earlier one (see matchPendingTrade).
  consumedOrderUuids: Record<string, true>;
};

// store
export const usePendingTradesStore = createWithEqualityFn(
  combine(
    { trades: {} as Record<string, PendingTrade>, consumedOrderUuids: {} as Record<string, true> } as PendingTradesState,
    (set, get) => ({
      addPendingTrade: (input: NewPendingTrade) => {
        const txHash = input.txHash.toLowerCase();
        // De-dupe: a tx hash already tracked is a no-op, don't clobber its fields
        // (e.g. a re-fired submit handler racing the same confirmed tx).
        if (get().trades[txHash]) return;
        const trade: PendingTrade = {
          ...input,
          txHash,
          marketAddress: input.marketAddress.toLowerCase(),
          optionAddress: input.optionAddress.toLowerCase(),
          baseTokenAddress: input.baseTokenAddress.toLowerCase(),
          status: 'pending',
          createdAt: input.createdAt ?? Date.now(),
        };
        set(state => ({ trades: { ...state.trades, [txHash]: trade } }));
      },
      markSyncSlow: (txHash: string) => {
        const key = txHash.toLowerCase();
        const trade = get().trades[key];
        if (!trade || trade.status !== 'pending') return;
        set(state => ({ trades: { ...state.trades, [key]: { ...trade, status: 'syncSlow' } } }));
      },
      removePendingTrade: (txHash: string) => {
        const key = txHash.toLowerCase();
        if (!(key in get().trades)) return;
        set(state => {
          const next = { ...state.trades };
          delete next[key];
          return { trades: next };
        });
      },
      // Marks a backend order uuid as spent so a later reconcile pass can't
      // reuse it to satisfy a different (e.g. repeat) pending trade.
      markOrderConsumed: (uuid: string) => {
        set(state => ({ consumedOrderUuids: { ...state.consumedOrderUuids, [uuid]: true } }));
      },
      clearAllPendingTrades: () => set({ trades: {}, consumedOrderUuids: {} }),
    })
  ),
  shallow
);

// Gate check for "disable trading this market while a prior trade of ours is
// unconfirmed" — syncSlow deliberately excluded, otherwise a 90s-unmatched
// trade would permanently lock the button.
export const hasPendingForMarket = (trades: Record<string, PendingTrade>, marketAddress?: string): boolean => {
  if (!marketAddress) return false;
  const market = marketAddress.toLowerCase();
  return Object.values(trades).some(trade => trade.status === 'pending' && trade.marketAddress === market);
};

export const selectPendingFor = (
  trades: Record<string, PendingTrade>,
  filter: { uid?: string; marketAddresses?: string[] }
): PendingTrade[] => {
  const marketSet = filter.marketAddresses?.map(addr => addr.toLowerCase());
  return Object.values(trades)
    .filter(trade => {
      if (filter.uid !== undefined && trade.uid !== filter.uid) return false;
      if (marketSet !== undefined && !marketSet.includes(trade.marketAddress)) return false;
      return true;
    })
    .sort((a, b) => b.createdAt - a.createdAt);
};

// React convenience hook. Caller should useMemo the filter object for
// referential stability (avoids recomputing selectPendingFor's array/sort on
// every render); the shallow equality check below is what actually protects
// against extra re-renders regardless.
export const usePendingTradesFor = (filter: { uid?: string; marketAddresses?: string[] }): PendingTrade[] =>
  usePendingTradesStore(state => selectPendingFor(state.trades, filter), shallow);

type ReconcileOrder = {
  uuid: string;
  uid: string;
  side: OrderSide;
  amount: string;
  timestamp: string;
  option?: { marketAddress?: string; address?: string };
};

const amountMatches = (orderAmount: string, pendingAmountIn: string): boolean => {
  if (orderAmount === pendingAmountIn) return true;
  const denom = Number(pendingAmountIn);
  if (denom === 0) return false; // zero denom: only exact string match accepted
  const num = Number(orderAmount);
  if (!Number.isFinite(num)) return false;
  return Math.abs(num - denom) / Math.abs(denom) <= 0.01;
};

// Reconciliation match: no txHash on backend order rows (FACT-1), so fall
// back to uid + side + market + option + amount(~1%) + time-window(-30s from
// submit — just enough to cover block-timestamp vs. wall-clock skew, not
// enough to reach back into a prior trade on the same market/option/amount).
// consumedOrderUuids additionally excludes any order uuid that already
// satisfied an earlier pending trade this session, so a rapid repeat trade
// (same uid/side/market/option/amount within the window) can't have its
// optimistic row prematurely cleared by the PREVIOUS trade's backend row.
const RECONCILE_LOOKBACK_MS = 30_000;

export const matchPendingTrade = (
  pending: PendingTrade,
  orders: ReconcileOrder[],
  consumedOrderUuids: Record<string, true> = {}
): string | undefined => {
  for (const order of orders) {
    if (consumedOrderUuids[order.uuid]) continue;

    if (order.uid !== pending.uid) continue;

    const sideMatches = pending.side === 'buy' ? order.side === OrderSide.deposit : order.side === OrderSide.withdraw;
    if (!sideMatches) continue;

    const orderMarket = order.option?.marketAddress;
    if (orderMarket && orderMarket.toLowerCase() !== pending.marketAddress) continue;

    const orderOption = order.option?.address;
    if (orderOption && pending.optionAddress && orderOption.toLowerCase() !== pending.optionAddress) continue;

    let tsMs = Number(order.timestamp);
    if (!Number.isFinite(tsMs)) continue;
    if (tsMs < 1e12) tsMs *= 1000; // seconds → ms
    if (tsMs < pending.createdAt - RECONCILE_LOOKBACK_MS) continue;

    if (!amountMatches(order.amount, pending.amountIn)) continue;

    return order.uuid;
  }
  return undefined;
};

const RECONCILE_DELAYS = [2000, 3000, 5000, 8000, 13000];

// Backoff schedule for reconcile polling attempts; out-of-range clamps to the
// last (longest) step.
export const nextReconcileDelay = (attempt: number): number => RECONCILE_DELAYS[attempt] ?? 13000;

export const RECONCILE_TIMEOUT_MS = 90_000;
